export interface PartnerItem {
  name: string;
  badge: string;
  type?: string;
  description?: string;
  highlights?: string[];
  website: string;
  logo?: string;
}

export interface TrustedByItem {
  name: string;
  logo: string;
}

export const partnersHero = {
  eyebrow: 'PARTNERS',
  titleBefore: 'Building biomedical innovation through ',
  titleHighlight: 'trusted',
  titleAfter: ' collaborations',
  subtitle:
    'We partner with specialized organizations to accelerate translational research and deliver measurable impact for life-science teams.',
} as const;

export const partnershipPillars = [
  {
    title: 'Scientific depth',
    body: 'Collaborations grounded in domain expertise, high-quality data, and reproducible evidence.',
  },
  {
    title: 'Technology fit',
    body: 'Joint workstreams that align with your stack, workflows, and delivery milestones.',
  },
  {
    title: 'Shared outcomes',
    body: 'Partnership models designed around practical goals: speed, reliability, and adoption.',
  },
] as const;

export const currentPartners: PartnerItem[] = [
  {
    name: 'Molsis',
    badge: 'MO',
    type: 'Software Partner',
    description:
      'MOLSIS Inc. is a Japan-based software company and a specialized professional group providing solutions for the life science and materials science communities. With over three decades of experience, MOLSIS offers advanced solutions in molecular modeling, molecular simulation, and informatics system. The company supports drug discovery and bioinformatics research by providing advanced computational tools, localized technical support, and comprehensive application training. As a trusted partner to global technology providers, MOLSIS helps researchers accelerate innovation efficiently through computationally driven science.',
    highlights: ['Molecular modeling', 'Molecular simulation', 'Informatics system'],
    website: 'https://www.molsis.co.jp/',
    logo: '/static/images/partners/MOLSIS-VNW2.png',
  },
  {
    name: 'LeadPath',
    badge: 'LP',
    type: 'Strategic Partner',
    description:
      'LeadPath Healthcare Innovations is a purpose-driven organization on a mission to transform the landscape of rare disease care. We stand by the principle that no patient with a rare disease should be left behind, especially those who remain undiagnosed or in under-resourced communities. Guided by our ethos “Navigating the Path Less Taken,” we champion innovative solutions and compassionate advocacy to ensure even the most challenging health journeys are met with hope and support. LeadPath actively supports several key rare disease areas, including Fibrodysplasia Ossificans Progressiva (FOP), Angelman syndrome, muscular dystrophy disorders, Prader-Willi Syndrome (PWS) and others. In parallel, we leverage our expertise in clinical genomics to accelerate rare disease diagnoses and research. By addressing both the medical and human challenges of rare diseases, we strive to bring tangible solutions and renewed hope to patients who previously had none.',
    highlights: ['Clinical research', 'Precision medicine'],
    website: 'https://leadpath.net',
    logo: '/static/images/partners/leadpath-logo.webp',
  },
  {
    name: 'CloudScientific',
    badge: 'CS',
    type: 'Software Partner',
    description:
      'CloudScientific is a Chinese company specialized in serving the life sciences community by providing integrated scientific software total solutions from all over the world to accelerate ground-breaking discovery and research locally in China. We have established long-term partnerships with recognized software and content providers, our solutions cover from computational chemistry/biology, drug design, cheminformatics, bioinformatics, proteomics, materials science, toxicology, knowledge base, AI and to informatics management systems.',
    highlights: ['Scientific software', 'Integrated solutions'],
    website: 'https://www.cloudscientific.com/',
    logo: '/static/images/partners/CloudScientific logo.png',
  },
  {
    name: 'Sapio',
    badge: 'SP',
    type: 'Software Partner',
    description:
      'Sapio Sciences delivers the Sapio Platform, an agentic AI lab informatics platform that makes life in the lab easier and more productive for scientists. The unified, configurable, low code and scalable environment brings together Sapio LIMS, the market’s most advanced and flexible LIMS for automating research, diagnostics and manufacturing, Sapio ELaiN, the third generation AI lab notebook and scientific co scientist, and Sapio Scientific Data Cloud, the scientific data unification solution with built in organization, search, charting, tools and AI. Biopharma R&D, biotech, CRO and clinical diagnostics organizations use Sapio to run complex workflows and keep samples, experiments and data connected in one place instead of juggling disconnected systems.',
    highlights: ['Scientific software', 'Integrated solutions'],
    website: 'https://www.sapiosciences.com/',
    logo: '/static/images/partners/sapio-logo-color_2.jpg',
  },
];

export const trustedBy: TrustedByItem[] = [
  { name: 'Alexion', logo: '/static/images/partners/Alexion-Logo.png' },
  { name: 'Cerevel', logo: '/static/images/partners/cerevel-logo.png' },
  // {
  //   name: 'Kyowa Kirin',
  //   logo: '/static/images/partners/KyowaKirin_BrandLogo_colorv2-PhotoRoom-1-1024x216.png',
  // },
  { name: 'Servier', logo: '/static/images/partners/company-servier.png' },
];

export const collaborations: PartnerItem[] = [
  {
    name: 'The NVIDIA Inception Program',
    badge: 'NI',
    type: 'Program Collaboration',
    highlights: ['Startup acceleration', 'Innovation and growth'],
    website: 'https://www.nvidia.com/en-us/startups/',
    logo: '/static/images/partners/nvidia-inception-logo.png',
  },
  {
    name: 'Pistoia Alliance',
    badge: 'PA',
    type: 'Alliance',
    highlights: ['Drug discovery', 'Collaboration'],
    website: 'https://pistoiaalliance.org/',
    logo: '/static/images/partners/new-pisto-logo.png',
  },
  {
    name: 'ASEBIO',
    badge: 'AS',
    type: 'Organization',
    highlights: ['Biotechnology', 'Collaboration'],
    website: 'https://www.asebio.com/',
    logo: '/static/images/partners/asebio-logo.png',
  },
];

export const partnersCta = {
  titleBefore: 'Looking for a ',
  titleHighlight: 'strategic',
  titleAfter: ' partner in biomedical AI?',
  body: 'Tell us about your partnership idea and we will shape a collaboration model that fits your roadmap.',
  buttonLabel: 'Start a conversation',
  buttonTo: '/book-a-demo' as const,
} as const;
